import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import Plot from 'react-plotly.js';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Eye,
  RefreshCw,
  Tags,
  Users,
} from 'lucide-react';
import { posthogAPI, zohoAPI } from '../services/api';

const parsePlot = (chart) => {
  if (!chart) return null;
  try {
    return JSON.parse(chart);
  } catch (error) {
    console.error('Failed to parse chart payload', error);
    return null;
  }
};

const ZohoLeads = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('12m');
  const [webPeriod, setWebPeriod] = useState('30d');
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [webSummary, setWebSummary] = useState(null);
  const [webCharts, setWebCharts] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [summaryResult, chartsResult, webSummaryResult, webChartsResult] = await Promise.all([
        zohoAPI.getSummary(),
        zohoAPI.getCharts(period),
        posthogAPI.getSummary(Number.parseInt(webPeriod, 10)),
        posthogAPI.getCharts(webPeriod),
      ]);

      setSummary(summaryResult);
      setCharts(chartsResult);
      setWebSummary(webSummaryResult);
      setWebCharts(webChartsResult);
    } catch (err) {
      console.error('Error loading Zoho analytics data:', err);
      setError('Failed to load Zoho analytics data.');
    } finally {
      setLoading(false);
    }
  }, [period, webPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.allSettled([zohoAPI.refresh(), posthogAPI.refresh()]);
      await loadData();
    } finally {
      setRefreshing(false);
    }
  };

  const pieChart = useMemo(() => parsePlot(charts?.pie_chart), [charts]);
  const barChart = useMemo(() => parsePlot(charts?.bar_chart), [charts]);
  const webChart = useMemo(() => parsePlot(webCharts?.line_chart), [webCharts]);

  const statusRows = useMemo(() => {
    if (!summary?.by_status) return [];
    return Object.entries(summary.by_status).sort((a, b) => b[1] - a[1]);
  }, [summary]);

  const earliest = summary?.date_range?.earliest
    ? new Date(summary.date_range.earliest).toLocaleDateString()
    : 'N/A';
  const latest = summary?.date_range?.latest
    ? new Date(summary.date_range.latest).toLocaleDateString()
    : 'N/A';
  const lastUpdated = summary?.last_updated
    ? new Date(summary.last_updated).toLocaleString()
    : 'N/A';

  const trend = webSummary?.trend_percent ?? 0;
  const TrendIcon = trend > 0 ? ArrowUp : trend < 0 ? ArrowDown : Activity;
  const trendClass = trend > 0 ? 'text-success' : trend < 0 ? 'text-danger' : 'text-secondary';

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="loading-spinner">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading Zoho leads analytics...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={loadData}>
            <RefreshCw size={16} className="me-2" />
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="floating-action">
        <Button variant="warning" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={16} className={`me-2 ${refreshing ? 'spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </div>

      <Row className="mb-4">
        <Col>
          <h1 className="d-flex align-items-center">
            <Users className="me-2 text-warning" />
            Zoho Leads Analytics
          </h1>
          <p className="text-muted mb-1">
            Track Zoho CRM leads, status distribution, and monthly lead creation trends.
          </p>
          <p className="text-muted mb-0">Includes website traffic metrics from PostHog.</p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xl={4} md={6}>
          <Card className="stats-card stats-card-warm text-center">
            <Card.Body>
              <Users className="mb-2" />
              <h3>{summary?.total_leads?.toLocaleString() ?? 0}</h3>
              <div>Total Leads</div>
              <small>{earliest} - {latest}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4} md={6}>
          <Card className="stats-card stats-card-warm text-center">
            <Card.Body>
              <Tags className="mb-2" />
              <h3>{statusRows.length}</h3>
              <div>Status Types</div>
              <small>Distinct lead stages</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4} md={6}>
          <Card className="stats-card stats-card-warm text-center">
            <Card.Body>
              <RefreshCw className="mb-2" />
              <h6 className="mb-1">{lastUpdated}</h6>
              <div>Last Updated</div>
              <small>Zoho cache refresh time</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h4 className="text-primary">Website Traffic Analytics</h4>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xl={4} md={6}>
          <Card className="stats-card stats-card-web text-center">
            <Card.Body>
              <Eye className="mb-2" />
              <h3>{webSummary?.total_visitors?.toLocaleString() ?? 0}</h3>
              <div>Total Visitors</div>
              <small>Last {webSummary?.period_days ?? 0} days</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4} md={6}>
          <Card className="stats-card stats-card-web text-center">
            <Card.Body>
              <Activity className="mb-2" />
              <h3>{webSummary?.average_daily?.toLocaleString() ?? 0}</h3>
              <div>Daily Average</div>
              <small>Unique visitors per day</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4} md={6}>
          <Card className="stats-card stats-card-web text-center">
            <Card.Body>
              <TrendIcon className={`mb-2 ${trendClass}`} />
              <h3 className={trendClass}>{trend.toFixed(1)}%</h3>
              <div>Trend</div>
              <small>vs previous period</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Daily Unique Visitors</h5>
              <div className="d-flex gap-2">
                {['7d', '30d', '90d'].map((value) => (
                  <Button
                    key={value}
                    size="sm"
                    variant={webPeriod === value ? 'success' : 'outline-success'}
                    onClick={() => setWebPeriod(value)}
                  >
                    {value.toUpperCase()}
                  </Button>
                ))}
              </div>
            </Card.Header>
            <Card.Body>
              {webChart ? (
                <Plot
                  data={webChart.data}
                  layout={{ ...webChart.layout, autosize: true }}
                  config={{ responsive: true, displaylogo: false }}
                  style={{ width: '100%', height: '420px' }}
                />
              ) : (
                <p className="text-muted mb-0">{webCharts?.message || 'No PostHog chart data available.'}</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Leads by Status</h5>
            </Card.Header>
            <Card.Body>
              {pieChart ? (
                <Plot
                  data={pieChart.data}
                  layout={{ ...pieChart.layout, autosize: true }}
                  config={{ responsive: true, displaylogo: false }}
                  style={{ width: '100%', height: '420px' }}
                />
              ) : (
                <p className="text-muted mb-0">No Zoho chart data available.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Leads Over Time</h5>
              <div className="d-flex gap-2 flex-wrap">
                {['6m', '12m', '2y', 'all'].map((value) => (
                  <Button
                    key={value}
                    size="sm"
                    variant={period === value ? 'secondary' : 'outline-secondary'}
                    onClick={() => setPeriod(value)}
                  >
                    {value.toUpperCase()}
                  </Button>
                ))}
              </div>
            </Card.Header>
            <Card.Body>
              {barChart ? (
                <Plot
                  data={barChart.data}
                  layout={{ ...barChart.layout, autosize: true }}
                  config={{ responsive: true, displaylogo: false }}
                  style={{ width: '100%', height: '420px' }}
                />
              ) : (
                <p className="text-muted mb-0">No Zoho trend data available.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Lead Status Breakdown</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Count</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {statusRows.length > 0 ? statusRows.map(([status, count]) => {
                    const pct = summary.total_leads ? (count / summary.total_leads) * 100 : 0;
                    return (
                      <tr key={status}>
                        <td>{status}</td>
                        <td>{count.toLocaleString()}</td>
                        <td>
                          <Badge bg="warning" text="dark">{pct.toFixed(1)}%</Badge>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">No lead status data available.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ZohoLeads;
